import React from 'react';
import { motion } from 'framer-motion';

interface SectionProps {
  id?: string;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  contentClassName?: string;
  containerMaxW?: string;
  /**
   * When true, children are rendered directly inside the section element
   * instead of being wrapped in the centered container. Useful for
   * full-bleed hero sections with absolute backgrounds.
   */
  disableContainer?: boolean;
}

/**
 * Section
 * A small wrapper to centralize section layout and typographic styles.
 * Use the `title` and `subtitle` props for consistent headings across pages.
 */
const SectionComponent: React.FC<SectionProps> = ({
  id,
  title,
  subtitle,
  children,
  className = '',
  titleClassName,
  subtitleClassName,
  contentClassName = '',
  containerMaxW = 'max-w-6xl',
  disableContainer = false,
}) => {
  return (
    <section id={id} className={`py-20 px-4 ${className}`}>
      {disableContainer ? (
        // Render children directly when the centered container would be limiting
        <>
          {title && (
            <motion.h2
              className={
                titleClassName ??
                'text-4xl md:text-5xl font-bold text-white text-center mb-12'
              }
              initial={{ opacity: 0, y: -40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {title}
            </motion.h2>
          )}

          {subtitle && (
            <motion.div
              className={
                subtitleClassName ?? 'text-xl md:text-2xl text-gray-300 text-center max-w-2xl mx-auto mb-8'
              }
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {subtitle}
            </motion.div>
          )}

          <div className={contentClassName}>{children}</div>
        </>
      ) : (
        <div className={`mx-auto ${containerMaxW}`}>
          {title && (
            <motion.h2
              className={
                titleClassName ??
                'text-4xl md:text-5xl font-bold text-white text-center mb-12'
              }
              initial={{ opacity: 0, y: -40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {title}
            </motion.h2>
          )}

          {subtitle && (
            <motion.div
              className={
                subtitleClassName ?? 'text-xl md:text-2xl text-gray-300 text-center max-w-2xl mx-auto mb-8'
              }
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {subtitle}
            </motion.div>
          )}

          <div className={contentClassName}>{children}</div>
        </div>
      )}
    </section>
  );
};
const SubSection: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className = '' }) => (
  <div className={`max-w-2xl mx-auto ${className}`}>{children}</div>
);

const Content: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className = '' }) => (
  <div className={`mx-auto ${className}`}>{children}</div>
);

type SectionType = typeof SectionComponent & {
  SubSection: typeof SubSection;
  Content: typeof Content;
};

const Section = SectionComponent as SectionType;
Section.SubSection = SubSection;
Section.Content = Content;

export default Section;
